// TradeMarginReviews — Chad's list on the Commission board (Greg, Oct 29, 2026: "We need to track the other
// trades with a 30% margin. Whatever is lacking from that margin needs to be flagged for review by Chad",
// "Commission board"). Each row is a sub's trade whose price is short of the target, done at Bardin's cost,
// or a detach and reset sold outside the claim after the insurance scope paid for it ("He removes that from
// scope as not completing. Then adds as retail for cheaper."). Chad approves it as is or sends it back to
// Greg, later: "Usually the deal is already made with homeowner before Chad sees it ... So Chad is just
// reviewing and removing the loss from the commission or overriding and allowing with no recourse." Two
// buttons: deduct the shortfall (the gap to the 30% floor; it joins the deal's non-collect BUCKET with any
// non-collect revenue — the first $500 of the bucket is forgiven and what's over splits RM / TL / ASM; Greg,
// Oct 29: "let it blend into non-collect revenue") or allow as is. Rule 4 (Greg: "No need to show JAC Gutters'
// price. Just show the 30% floor"): this list is the one place that keeps the sub's price, beside the floor.
// Everyone on the board reads the list; only Chad and the admin tier press the buttons (the server checks too:
// build-calendar trade_margin_review).

import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import DealLink from "../DealLink";
import { useRealtimeTable } from "../../lib/useRealtimeTable";
import {
  BILLED_TO_LABELS, WORK_LABELS, canReviewMargins, fetchMarginReviewQueue, fmtMoney2, fmtPct, fmtTradeDay, marginShortfall, priceAtMargin, tradeCall,
  type MarginReviewRow,
} from "../../lib/trades";
import { useStaffNames } from "../trades/useTradeModals";

export default function TradeMarginReviews({ onSettled }: { onSettled?: () => void }) {
  const { role, isSuperUser } = useAuth();
  const canAct = canReviewMargins(role, isSuperUser);
  const [rows, setRows] = useState<MarginReviewRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const names = useStaffNames();

  const load = useCallback(async () => {
    try {
      setRows(await fetchMarginReviewQueue());
      setErr(null);
    } catch (e) {
      // RLS answers no rows to someone kept off prices; anything else shows.
      setErr(e instanceof Error ? e.message : "Couldn't load the trade prices.");
    }
  }, []);
  useEffect(() => { void load(); }, [load]);
  useRealtimeTable("trade_items", () => { void load(); });

  async function decide(tradeId: string, decision: "allow" | "deduct", amount: number | null) {
    if (decision === "deduct" && !window.confirm(`Deduct ${fmtMoney2(amount ?? 0)}? It joins the deal's non-collect bucket with any non-collect revenue; the first $500 of the bucket is forgiven and what's over comes off the commissions, split RM / TL / ASM.`)) return;
    setBusy(tradeId);
    try {
      await tradeCall({ action: "trade_margin_review", trade_id: tradeId, decision, note: notes[tradeId] ?? "" });
      await load();
      // The board's deals (and so the pool, the banner and Approve) reload too (delta review, Oct 29: a deduction
      // followed by Approve on the same card paid the commission without it).
      onSettled?.();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  if (rows === null && !err) return null;
  const list = rows ?? [];

  return (
    <section className="mt-6">
      <h2 className="text-sm font-semibold text-bardin-ink mb-1">🔧 Trade prices to settle{list.length ? ` · ${list.length}` : ""}</h2>
      <p className="text-[12px] text-slate-500 mb-3">
        Subs' trades short of the 30% floor, at Bardin's cost, or sold outside the claim after insurance paid for the detach and reset.
        Deduct the shortfall — it joins the deal's bucket with any non-collect revenue; the first $500 of the bucket is forgiven and the
        rest splits RM / TL / ASM — or allow it with no recourse. Above the floor, what a homeowner-billed trade charges over it is the
        trade upside pool, paid as commission (the RM's unless you share it with ✎ Edit).
      </p>
      {err && <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">{err}</div>}
      {!err && list.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-200 px-3 py-4 text-sm text-slate-400 text-center">Nothing waiting on a review.</div>
      )}
      <div className="space-y-2">
        {list.map((r) => {
          const t = r.trade;
          const short = r.margin_pct !== null && r.margin_target_pct !== null && r.margin_pct < r.margin_target_pct;
          const target = r.margin_target_pct ?? 30;
          const shortfall = marginShortfall(r.agreed_total, r.customer_total, target);
          // Rule 4: the floor (sub cost ÷ 0.7 at 30%) beside the sub's price; above it the difference is the RM's upside pool
          // on a homeowner-billed trade (an insurance-billed one sits inside the scope — no pool).
          const floor = r.agreed_total !== null && r.agreed_total > 0 ? priceAtMargin(r.agreed_total, target) : null;
          const upside = floor !== null && r.customer_total !== null ? Math.round(Math.max(0, r.customer_total - floor) * 100) / 100 : 0;
          // Once the commission is approved its lines are stored and the Books bill made, so the pool can't change
          // what is paid: the server refuses a deduction then (delta review, Oct 29).
          const commission = t.deal?.commission_paid ? "paid" : t.deal?.commission_approved ? "approved" : null;
          return (
            <div key={r.trade_id} className="rounded-xl border border-amber-300 bg-white p-3 text-sm">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-bardin-ink">
                    <DealLink id={t.deal_id}>{t.deal?.homeowner_name ?? "(no name)"}</DealLink>
                    <span className="font-normal text-slate-500"> · {[t.deal?.property_street, t.deal?.property_city].filter(Boolean).join(", ")}</span>
                  </div>
                  <div className="text-[12.5px] text-slate-700 mt-0.5">
                    {WORK_LABELS[t.trade_type]}{t.vendor ? ` · ${t.vendor}` : ""}{t.scheduled_date ? ` · ${fmtTradeDay(t.scheduled_date)}` : " · no day yet"}
                    {t.billed_to ? ` · paid by ${BILLED_TO_LABELS[t.billed_to].toLowerCase()}` : ""}
                  </div>
                </div>
                <span className="text-[10.5px] font-bold px-2 py-0.5 rounded-full shrink-0 bg-amber-100 text-amber-800">Needs Chad</span>
              </div>

              <div className="mt-2 grid grid-cols-2 sm:grid-cols-5 gap-2 text-[12px]">
                <div className="rounded-lg bg-slate-50 px-2 py-1.5">
                  <div className="text-slate-500">Sub's price</div>
                  <div className="font-semibold tabular-nums text-slate-800">{r.agreed_total !== null ? fmtMoney2(r.agreed_total) : "waiting on a quote"}</div>
                  {r.price_status === "agreed" && r.list_total !== null && r.list_total !== r.agreed_total && <div className="text-slate-400">list {fmtMoney2(r.list_total)}</div>}
                </div>
                <div className="rounded-lg bg-slate-50 px-2 py-1.5">
                  <div className="text-slate-500">{fmtPct(target)} floor</div>
                  <div className="font-semibold tabular-nums text-slate-800">{floor !== null ? fmtMoney2(floor) : "waiting on a quote"}</div>
                </div>
                <div className="rounded-lg bg-slate-50 px-2 py-1.5">
                  <div className="text-slate-500">Customer</div>
                  <div className="font-semibold tabular-nums text-slate-800">{r.customer_total !== null ? fmtMoney2(r.customer_total) : "not set"}</div>
                </div>
                <div className="rounded-lg bg-slate-50 px-2 py-1.5">
                  <div className="text-slate-500">Margin</div>
                  <div className={`font-semibold tabular-nums ${short ? "text-rose-700" : "text-slate-800"}`}>
                    {r.margin_pct !== null ? fmtPct(r.margin_pct) : "—"}{r.margin_target_pct !== null ? <span className="font-normal text-slate-400"> / {fmtPct(r.margin_target_pct)} target</span> : null}
                  </div>
                </div>
                <div className="rounded-lg bg-slate-50 px-2 py-1.5">
                  <div className="text-slate-500">{upside > 0 ? "Above the floor" : "Short of the floor"}</div>
                  {upside > 0 ? (
                    <div className="font-semibold tabular-nums text-teal-700">
                      +{fmtMoney2(upside)} upside{t.billed_to === "homeowner" ? " → RM pool" : t.billed_to === "insurance" ? " (in the scope, no pool)" : ""}
                    </div>
                  ) : (
                    <div className="font-semibold tabular-nums text-slate-800">{shortfall !== null ? fmtMoney2(shortfall) : "waiting on the sub"}</div>
                  )}
                </div>
              </div>

              {r.margin_review_reason && <div className="mt-2 text-[12px] text-amber-900">⚠ {r.margin_review_reason}</div>}
              {t.status === "complete" && <div className="mt-1 text-[12px] text-slate-500">The work is done; the price still needs settling.</div>}
              {commission && (
                <div className="mt-1 text-[12px] text-rose-800">
                  This deal's commission is already {commission}{commission === "paid" ? ", so nothing comes off it now: book the shortfall as a draw instead, or allow the price." : " (its Books bill is made): unapprove it below first to deduct, or allow the price."}
                </div>
              )}
              {r.lines.length > 0 && (
                <div className="mt-1.5 text-[11.5px] text-slate-500">
                  {r.lines.map((l) => `${l.label}${l.qty ? ` ${l.qty} ${l.unit === "sq" ? "sq" : "ft"}` : ""}${l.amount !== null ? ` ${fmtMoney2(l.amount)}` : ""}`).join(" · ")}
                </div>
              )}
              {t.scope_notes && <div className="mt-1.5 text-[12px] text-slate-700 whitespace-pre-wrap line-clamp-3">{t.scope_notes}</div>}
              <div className="mt-1.5 text-[11px] text-slate-400">
                Set up by {t.assigned_by ? names[t.assigned_by] ?? "someone" : "hand"} {fmtTradeDay(t.created_at.slice(0, 10))}
              </div>

              {canAct && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <input value={notes[r.trade_id] ?? ""} onChange={(e) => setNotes((n) => ({ ...n, [r.trade_id]: e.target.value }))} maxLength={500} placeholder="Note (optional)"
                         className="min-w-0 flex-1 basis-40 border border-slate-300 rounded px-2 py-1.5 text-sm" />
                  <button type="button" disabled={busy === r.trade_id || shortfall === null || shortfall <= 0 || !!commission} onClick={() => void decide(r.trade_id, "deduct", shortfall)}
                          title={shortfall === null ? "The sub's price isn't known yet" : shortfall <= 0 ? "Nothing is lacking; allow it" : commission ? `The commission is already ${commission}` : undefined}
                          className="px-3 py-1.5 rounded bg-rose-600 text-white text-xs font-semibold disabled:opacity-50">
                    Deduct {shortfall !== null && shortfall > 0 ? fmtMoney2(shortfall) : ""} from the commission
                  </button>
                  <button type="button" disabled={busy === r.trade_id} onClick={() => void decide(r.trade_id, "allow", null)}
                          className="px-3 py-1.5 rounded bg-emerald-600 text-white text-xs font-semibold disabled:opacity-50">Allow as is, no recourse</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
